/**
 * @fileoverview System Inspection Programs admin page (issue #737 Phase 6,
 *   plan §11.5 steps 56–57).
 * @module system-web/pages/InspectionProgramsPage
 *
 * Responsibilities:
 * - Render the canonical `systemInspectionPrograms` catalog with edit +
 *   activate/deactivate actions and a manual "Propagate to All Tenants"
 *   trigger.
 * - Render the `systemScheduleRuleCatalog` on a second tab with edit/add.
 *
 * Dependencies:
 * - ../hooks/useSystemInspectionPrograms (liveQuery + mutations)
 * - ../components/ui/ConfirmActionModal
 *
 * Architecture:
 * - Local-first: the grids read Dexie via liveQuery; writes go through
 *   system-api which propagates to tenant DBs via the canonical replay.
 *
 * @component InspectionProgramsPage
 * Purpose:     Manage the 12 predefined inspection programs + the rule-type
 *              catalog that tenants configure against.
 * Props:       none (route component).
 * Behavior:    Programs tab: list + edit drawer + activate/deactivate.
 *              Rule Catalog tab: list + edit drawer + add entry.
 *              "Propagate to All Tenants" button triggers the manual replay
 *              and reports per-catalog counts.
 * Data Source: hybrid — Dexie liveQuery reads; system-api writes.
 * Constraints: programKey/ruleTypeCode immutable once created.
 * Performance: small static lists; no virtualization needed.
 */

import React, { useMemo, useState } from 'react';
import { CalendarClock, ListChecks, Pencil, Plus, Power, RefreshCcw, X } from 'lucide-react';
import { useScheduleRuleCatalog, useSystemInspectionPrograms } from '../hooks/useSystemInspectionPrograms';
import type { SystemInspectionProgram, SystemInspectionProgramSlot } from '../services/systemInspectionProgramService';
import type { ScheduleRuleCatalogEntry } from '../services/scheduleRuleCatalogService';

const REOCCURRENCES = ['annual', 'biennial', 'triennial'] as const;
const DIVISIONS = ['monthly', 'quarterly', 'semiannual', 'annual'] as const;

const REOCCURRENCE_LABELS: Record<string, string> = {
  annual: 'Once a year',
  biennial: 'Every 2 years',
  triennial: 'Every 3 years',
};

const DIVISION_LABELS: Record<string, string> = {
  monthly: 'Monthly (12 slots)',
  quarterly: 'Quarterly (4 slots)',
  semiannual: 'Semiannual (2 slots)',
  annual: 'Annual (1 slot)',
};

type Tab = 'programs' | 'ruleCatalog';

export default function InspectionProgramsPage() {
  const [tab, setTab] = useState<Tab>('programs');
  const {
    programs,
    isLoading: programsLoading,
    isMutating: programsMutating,
    error: programsError,
    addProgram,
    updateProgram,
    setProgramActive,
    propagate,
    clearError: clearProgramsError,
  } = useSystemInspectionPrograms();
  const {
    entries,
    isLoading: catalogLoading,
    isMutating: catalogMutating,
    error: catalogError,
    addEntry,
    updateEntry,
    clearError: clearCatalogError,
  } = useScheduleRuleCatalog();

  const [editing, setEditing] = useState<Partial<SystemInspectionProgram> | null>(null);
  const [editingIsNew, setEditingIsNew] = useState(false);
  const [editingEntry, setEditingEntry] = useState<Partial<ScheduleRuleCatalogEntry> | null>(null);
  const [editingEntryIsNew, setEditingEntryIsNew] = useState(false);
  const [propagateResult, setPropagateResult] = useState<string | null>(null);
  const [propagating, setPropagating] = useState(false);

  const error = tab === 'programs' ? programsError : catalogError;
  const clearError = tab === 'programs' ? clearProgramsError : clearCatalogError;
  const isMutating = programsMutating || catalogMutating || propagating;

  const activeCount = useMemo(() => programs.filter((p) => p.active !== false).length, [programs]);

  // -------------------------------------------------------------------------
  // Program editing
  // -------------------------------------------------------------------------
  const openProgramEditor = (program?: SystemInspectionProgram) => {
    setEditingIsNew(!program);
    setEditing(
      program
        ? { ...program }
        : {
            programKey: '',
            name: '',
            description: '',
            reoccurrence: 'annual',
            division: 'annual',
            slots: [{ _id: 'annual', label: 'Annual', targetMonth: 1 }],
            applicableCategories: [],
            active: true,
          },
    );
  };

  const saveProgram = async () => {
    if (!editing || !editing.name?.trim()) return;
    const patch = {
      name: editing.name.trim(),
      description: editing.description?.trim() ?? '',
      reoccurrence: editing.reoccurrence,
      division: editing.division,
      slots: editing.slots ?? [],
      applicableCategories: editing.applicableCategories ?? [],
    };
    const result = editingIsNew
      ? await addProgram({ ...patch, programKey: editing.programKey?.trim() || undefined })
      : await updateProgram(String(editing._id), patch);
    if (result) setEditing(null);
  };

  const toggleActive = async (program: SystemInspectionProgram) => {
    await setProgramActive(String(program._id), !(program.active !== false));
  };

  // -------------------------------------------------------------------------
  // Rule catalog editing
  // -------------------------------------------------------------------------
  const openEntryEditor = (entry?: ScheduleRuleCatalogEntry) => {
    setEditingEntryIsNew(!entry);
    setEditingEntry(
      entry
        ? { ...entry }
        : { ruleTypeCode: '', name: '', description: '', active: true },
    );
  };

  const saveEntry = async () => {
    if (!editingEntry?.name?.trim()) return;
    if (editingEntryIsNew && !editingEntry.ruleTypeCode?.trim()) return;
    const patch = {
      name: editingEntry.name.trim(),
      description: editingEntry.description?.trim() ?? '',
      conditions: editingEntry.conditions ?? [],
      actions: editingEntry.actions ?? [],
      modes: editingEntry.modes ?? [],
      conflictResolutions: editingEntry.conflictResolutions ?? [],
      configurableFields: editingEntry.configurableFields ?? {},
      active: editingEntry.active !== false,
    };
    const result = editingEntryIsNew
      ? await addEntry({ ruleTypeCode: String(editingEntry.ruleTypeCode).trim(), ...patch })
      : await updateEntry(String(editingEntry._id), patch);
    if (result) setEditingEntry(null);
  };

  // -------------------------------------------------------------------------
  // Manual propagation
  // -------------------------------------------------------------------------
  const runPropagation = async () => {
    setPropagating(true);
    setPropagateResult(null);
    try {
      const result = await propagate('all');
      if (result) {
        const p = result.programs;
        const c = result.ruleCatalog;
        setPropagateResult(
          `Propagated — programs: ${p?.succeeded ?? 0}/${p?.totalTenants ?? 0} tenants` +
            ` (inserted ${p ? (p as any).inserted ?? 'n/a' : 'n/a'}),` +
            ` rule catalog: ${c?.succeeded ?? 0}/${c?.totalTenants ?? 0} tenants.` +
            ((p?.failed ?? 0) + (c?.failed ?? 0) > 0 ? ' Some tenants failed — see logs.' : ''),
        );
      }
    } finally {
      setPropagating(false);
    }
  };

  const reoccurrenceBadge = (value: string) => REOCCURRENCE_LABELS[value] ?? value;
  const divisionBadge = (value: string) => DIVISION_LABELS[value] ?? value;

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inspection Programs</h1>
          <p className="text-gray-500 mt-1">
            Predefined program catalog replayed to every department — {activeCount} of {programs.length} active
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={runPropagation}
            disabled={isMutating}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-orange-300 text-orange-700 text-sm font-medium hover:bg-orange-50 transition-colors disabled:opacity-50"
          >
            <RefreshCcw className={`h-4 w-4 ${propagating ? 'animate-spin' : ''}`} />
            Propagate to All Tenants
          </button>
          {tab === 'programs' ? (
            <button
              onClick={() => openProgramEditor()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add Predefined Program
            </button>
          ) : (
            <button
              onClick={() => openEntryEditor()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 transition-colors"
            >
              <Plus className="h-4 w-4" /> Add Rule Type
            </button>
          )}
        </div>
      </div>

      {propagateResult && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm">
          <span className="flex-1">{propagateResult}</span>
          <button type="button" onClick={() => setPropagateResult(null)} aria-label="Dismiss propagation result" className="text-blue-400 hover:text-blue-600">✕</button>
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={clearError} aria-label="Dismiss error" className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      <div className="flex gap-2 mb-4 border-b border-gray-200">
        {(
          [
            { key: 'programs', label: 'Programs', icon: CalendarClock },
            { key: 'ruleCatalog', label: 'Rule Catalog', icon: ListChecks },
          ] as const
        ).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key
                ? 'border-orange-600 text-orange-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === 'programs' && (
        programsLoading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : programs.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <CalendarClock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No predefined programs — the catalog seeds at startup or via propagate.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {programs.map((program) => (
              <div
                key={String(program._id)}
                className={`bg-white rounded-xl border p-5 transition-shadow hover:shadow-md ${
                  program.active === false ? 'border-gray-200 opacity-60' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">{program.name}</h3>
                    <p className="text-xs text-gray-400 font-mono">{program.programKey}</p>
                  </div>
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      program.active === false ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {program.active === false ? 'Inactive' : 'Active'}
                  </span>
                </div>
                {program.description && <p className="text-sm text-gray-500 mb-3">{program.description}</p>}
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
                  <dt className="text-gray-400">Reoccurrence</dt>
                  <dd>{reoccurrenceBadge(program.reoccurrence)}</dd>
                  <dt className="text-gray-400">Division</dt>
                  <dd>{divisionBadge(program.division)}</dd>
                  <dt className="text-gray-400">Slots</dt>
                  <dd>{program.slotCount ?? program.slots?.length ?? 0}</dd>
                  <dt className="text-gray-400">Categories</dt>
                  <dd>{program.applicableCategories?.length ? program.applicableCategories.join(', ') : 'All'}</dd>
                </dl>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => openProgramEditor(program)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    onClick={() => toggleActive(program)}
                    disabled={isMutating}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border disabled:opacity-50 ${
                      program.active === false
                        ? 'border-green-300 text-green-700 hover:bg-green-50'
                        : 'border-red-300 text-red-700 hover:bg-red-50'
                    }`}
                  >
                    <Power className="h-3.5 w-3.5" /> {program.active === false ? 'Activate' : 'Deactivate'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'ruleCatalog' && (
        catalogLoading ? (
          <div className="text-center py-12 text-gray-500">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <ListChecks className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No rule types in the catalog yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => (
              <div
                key={String(entry._id)}
                className={`bg-white rounded-xl border border-gray-200 p-5 ${entry.active === false ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-gray-900">{entry.name}</h3>
                      <code className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded">{entry.ruleTypeCode}</code>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          entry.active === false ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {entry.active === false ? 'Inactive' : 'Active'}
                      </span>
                    </div>
                    {entry.description && <p className="text-sm text-gray-500 mt-1">{entry.description}</p>}
                    <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
                      {(entry.conditions?.length ?? 0) > 0 && (
                        <span>Conditions: {entry.conditions!.map((c) => c.name).join(', ')}</span>
                      )}
                      {(entry.actions?.length ?? 0) > 0 && (
                        <span>Actions: {entry.actions!.map((a) => a.name).join(', ')}</span>
                      )}
                      {(entry.modes?.length ?? 0) > 0 && (
                        <span>Modes: {entry.modes!.map((m) => m.name).join(', ')}</span>
                      )}
                      {(entry.conflictResolutions?.length ?? 0) > 0 && (
                        <span>Resolutions: {entry.conflictResolutions!.map((r) => r.name).join(', ')}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => openEntryEditor(entry)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 ml-4"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* Program editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingIsNew ? 'Add Predefined Program' : `Edit ${editing.name}`}
                </h2>
                <p className="text-xs text-gray-500">Definition fields propagate to every tenant on save</p>
              </div>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close program editor" className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {editingIsNew && (
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Program Key</span>
                  <input
                    value={editing.programKey ?? ''}
                    onChange={(e) => setEditing({ ...editing, programKey: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm font-mono"
                    placeholder="annual-monthly (auto-generated if blank)"
                  />
                </label>
              )}
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Name *</span>
                <input
                  value={editing.name ?? ''}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Description</span>
                <textarea
                  value={editing.description ?? ''}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Reoccurrence</span>
                  <select
                    value={editing.reoccurrence}
                    onChange={(e) => setEditing({ ...editing, reoccurrence: e.target.value as SystemInspectionProgram['reoccurrence'] })}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                  >
                    {REOCCURRENCES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-gray-600 mb-1">Division</span>
                  <select
                    value={editing.division}
                    onChange={(e) => setEditing({ ...editing, division: e.target.value as SystemInspectionProgram['division'] })}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-white"
                  >
                    {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
              </div>
              <div>
                <span className="block text-xs font-medium text-gray-600 mb-1">Slots ({editing.slots?.length ?? 0})</span>
                <div className="space-y-2">
                  {(editing.slots ?? []).map((slot, i) => (
                    <div key={slot._id ?? i} className="flex items-center gap-2">
                      <input
                        value={slot.label}
                        onChange={(e) => {
                          const slots = [...(editing.slots ?? [])];
                          slots[i] = { ...slot, label: e.target.value };
                          setEditing({ ...editing, slots });
                        }}
                        className="flex-1 px-3 py-1.5 border rounded-lg text-sm"
                        placeholder="Slot label"
                      />
                      <input
                        type="number"
                        min={1}
                        max={12}
                        value={slot.targetMonth}
                        onChange={(e) => {
                          const slots = [...(editing.slots ?? [])];
                          slots[i] = { ...slot, targetMonth: Number(e.target.value) };
                          setEditing({ ...editing, slots });
                        }}
                        className="w-24 px-3 py-1.5 border rounded-lg text-sm"
                        placeholder="Month"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const slots = (editing.slots ?? []).filter((_, j) => j !== i);
                          setEditing({ ...editing, slots });
                        }}
                        className="text-gray-400 hover:text-red-500"
                        aria-label={`Remove slot ${slot.label}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const slots = [...(editing.slots ?? []), { _id: `slot-${Date.now()}`, label: '', targetMonth: 1 } satisfies SystemInspectionProgramSlot];
                      setEditing({ ...editing, slots });
                    }}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                  >
                    + Add slot
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={saveProgram}
                disabled={isMutating || !editing.name?.trim()}
                className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
              >
                {isMutating ? 'Saving…' : editingIsNew ? 'Create Program' : 'Save Program'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rule catalog entry editor drawer */}
      {editingEntry && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingEntryIsNew ? 'Add Rule Type' : `Edit ${editingEntry.name}`}
                </h2>
                <p className="text-xs text-gray-500">Catalog changes propagate to every tenant</p>
              </div>
              <button type="button" onClick={() => setEditingEntry(null)} aria-label="Close catalog editor" className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Rule Type Code *</span>
                <input
                  value={editingEntry.ruleTypeCode ?? ''}
                  onChange={(e) => setEditingEntry({ ...editingEntry, ruleTypeCode: e.target.value })}
                  disabled={!editingEntryIsNew}
                  className={`w-full px-3 py-2 border rounded-lg text-sm font-mono ${!editingEntryIsNew ? 'bg-gray-50 text-gray-500' : ''}`}
                  placeholder="camelCase — e.g. reinspectionProximity"
                />
                {!editingEntryIsNew && (
                  <p className="mt-1 text-xs text-gray-400">ruleTypeCode is immutable — tenant rules reference it.</p>
                )}
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Name *</span>
                <input
                  value={editingEntry.name ?? ''}
                  onChange={(e) => setEditingEntry({ ...editingEntry, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Description</span>
                <textarea
                  value={editingEntry.description ?? ''}
                  onChange={(e) => setEditingEntry({ ...editingEntry, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                />
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingEntry.active !== false}
                  onChange={(e) => setEditingEntry({ ...editingEntry, active: e.target.checked })}
                />
                <span className="text-sm text-gray-700">Active (available to tenants)</span>
              </label>
              <p className="text-xs text-gray-400">
                Conditions/actions/modes menus are edited via JSON on the API for now — the seeded
                entries already carry the canonical option sets.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button type="button" onClick={() => setEditingEntry(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={saveEntry}
                disabled={isMutating || !editingEntry.name?.trim() || (editingEntryIsNew && !editingEntry.ruleTypeCode?.trim())}
                className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 disabled:opacity-50"
              >
                {isMutating ? 'Saving…' : editingEntryIsNew ? 'Create Entry' : 'Save Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
